import React, {
	// createContext,
	memo,
	useState,
	useMemo,
	useCallback,
	useEffect,
	useRef,
} from "react";
import message from "../../utils/message";
import { motion } from "framer-motion";

import { SURFACE_DATA_KEY, SURFACE_TYPES } from "../../conf";
import SurveySurface from "../surfaces/SurveySurface";
import RewardSurface from "../surfaces/RewardSurface";
import CustomPostSurface from "../surfaces/CustomPostSurface";
import anim from "../../utils/anim";

import "./surface-orchestrator.css";
import useData from "../../hooks/useData";
import useDimensions from "../../hooks/useDimensions";
import { MESSAGE_TYPES } from "../../hooks/useMessage";
import inPreview from "../../utils/inPreview";

// export const SurfaceDimensionsContext = createContext();

const SURFACES = {
	[SURFACE_TYPES.CUSTOM_POST]: ({ surfaceData, idx, ...props }) => (
		<CustomPostSurface
			surfaceData={surfaceData}
			key={surfaceData?.remote_id || idx}
			idx={idx}
			{...props}
		/>
	),
	[SURFACE_TYPES.SURVEY]: ({ surfaceData, idx, ...props }) => (
		<SurveySurface
			surfaceData={surfaceData}
			key={surfaceData?.remote_id || idx}
			idx={idx}
			{...props}
		/>
	),
	[SURFACE_TYPES.REWARD]: ({ surfaceData, idx, ...props }) => (
		<RewardSurface
			surfaceData={surfaceData}
			key={surfaceData?.remote_id || idx}
			idx={idx}
			{...props}
		/>
	),
};

const SurfaceOrchestrator = memo(() => {
	const rootRef = useRef(null);

	const [fireOpenMessage, setFireOpenMessage] = useState(false);
	const [showFullCard, setShowFullCard] = useState(true);

	const { display_order, publisher_details } = useData();

	const { innerHeight, offsetTop, inDesktopOnly } = useDimensions();

	const [modals] = useState(display_order || []);
	const [activeSurface, setActiveSurface] = useState(
		display_order ? display_order.length - 1 : -1
	);

	const currSurfaceData = useMemo(
		() =>
			modals && modals.length > 0 && activeSurface > -1
				? {
						...modals[activeSurface][
							SURFACE_DATA_KEY[modals[activeSurface].field_name]
						],
						module_order: activeSurface,
				  }
				: null,
		[modals, activeSurface]
	);

	const onClose = useCallback(() => {
		message(MESSAGE_TYPES.CLOSE_OVERLAY_ORCHESTRATOR);
	}, []);

	const handleSurfaceClose = useCallback(() => {
		rootRef.current.scrollTop = 0;
		rootRef.current.focus();
		setActiveSurface((activeSurface) => {
			if (inPreview && activeSurface === 0) {
				return activeSurface;
			}
			return activeSurface - 1;
		});
	}, []);

	const handleOrchestratorClose = useCallback(() => {
		if (inPreview) {
			return handleSurfaceClose();
		}
		onClose();
	}, [handleSurfaceClose, onClose]);

	const handleScroll = useCallback(() => {
		if (!rootRef?.current) {
			return;
		}
		const scroll = rootRef.current.scrollTop;
		if (scroll > 10) {
			setShowFullCard(false);
		} else {
			setShowFullCard(true);
		}
	}, []);

	useEffect(() => {
		if (fireOpenMessage) {
			return;
		}
		message(MESSAGE_TYPES.OPEN_OVERLAY_ORCHESTRATOR);
		setFireOpenMessage(true);
	}, [fireOpenMessage]);

	useEffect(() => {
		if (currSurfaceData) {
			return;
		}
		onClose();
	}, [currSurfaceData, onClose]);

	const renderOrchestrator = useMemo(
		() => (
			// <SurfaceDimensionsContext.Provider
			// 	value={{
			// 		innerHeight: Number(innerHeight),
			// 		offsetTop: Number(offsetTop),
			// 	}}
			// >
			<motion.section
				className="surface-orchestrator-container"
				style={{
					top: `${offsetTop}px`,
					height: `${innerHeight}px`,
				}}
				ref={rootRef}
				onScroll={handleScroll}
				variants={anim.surfaceManager}
				initial="initial"
				animate="animate"
				exit="exit"
			>
				{currSurfaceData &&
					SURFACES[modals[activeSurface].field_name]({
						surfaceData: currSurfaceData,
						idx: activeSurface,
						handleSurfaceClose,
						handleOrchestratorClose,
						showFullCard,
						rootRef,
					})}
			</motion.section>
			// </SurfaceDimensionsContext.Provider>
		),
		[
			innerHeight,
			offsetTop,
			activeSurface,
			currSurfaceData,
			handleSurfaceClose,
			handleOrchestratorClose,
			showFullCard,
			handleScroll,
			modals,
		]
	);

	// TODO: QA Needed
	if (!currSurfaceData) return null;

	if (!inDesktopOnly) {
		return renderOrchestrator;
	}

	return (
		<section
			className="desktop-orchestrator-container"
			style={{
				backgroundColor:
					publisher_details?.modal_color && inDesktopOnly
						? publisher_details?.modal_color
						: "white",
			}}
		>
			<div className="desktop-orchestrator-container-card">
				{renderOrchestrator}
			</div>
		</section>
	);
});

export default SurfaceOrchestrator;
